<div class="search-header">
  <mat-form-field
    appearance="fill"
    subscriptSizing="dynamic"
    class="search-field"
  >
    <mat-icon matPrefix>search</mat-icon>
    <input
      matInput
      type="text"
      autocomplete="off"
      cdkFocusInitial
      role="combobox"
      aria-label="Search JTMS"
      aria-autocomplete="list"
      placeholder="Search projects, jobs, shipments, quotes, components, parts and containers"
      [attr.aria-expanded]="groups().length > 0"
      [attr.aria-controls]="listId"
      [attr.aria-activedescendant]="activeOptionId()"
      [value]="searchString()"
      (input)="searchString.set($any($event.target).value)"
      (keydown)="onKeydown($event)"
    />
    @if (searchString()) {
      <button
        matSuffix
        mat-icon-button
        type="button"
        aria-label="Clear search"
        (click)="searchString.set('')"
      >
        <mat-icon>close</mat-icon>
      </button>
    }
    <button
      matSuffix
      mat-icon-button
      type="button"
      aria-label="Scan a QR code or barcode"
      (click)="openScannerDialog()"
    >
      <mat-icon>qr_code_scanner</mat-icon>
    </button>
  </mat-form-field>
  @if (words().length) {
    <p class="search-hint">
      Matching every word:
      @for (word of words(); track $index) {
        <span class="search-hint-word">{{ word }}</span>
      }
    </p>
  }
</div>

@if (isLoading()) {
  <mat-progress-bar
    class="search-progress"
    mode="indeterminate"
    aria-label="Searching"
  />
}

<div
  class="search-results"
  role="listbox"
  aria-label="Search results"
  [id]="listId"
>
  @for (group of groups(); track group.label) {
    <div
      role="group"
      class="result-group"
      [attr.aria-label]="group.label"
    >
      <div
        class="group-header"
        aria-hidden="true"
      >
        <span>{{ group.label }} · {{ group.rows.length }}</span>
        @if (group.seeAll) {
          <a
            class="see-all"
            tabindex="-1"
            [routerLink]="group.seeAll"
            [queryParams]="{ query: searchString().trim() }"
            (click)="closeModal()"
            >See all in {{ group.label }}</a
          >
        } @else {
          <span
            class="see-all disabled"
            title="The {{ group.label }} table can't be opened with a search yet"
            >See all in {{ group.label }}</span
          >
        }
      </div>
      @for (row of group.rows; track row.key) {
        @let index = rowIndex().get(row.key) ?? 0;
        <div
          role="option"
          class="result-row"
          [id]="optionId(index)"
          [class.active]="index === activeIndex()"
          [attr.aria-selected]="index === activeIndex()"
          (click)="open(row)"
          (mouseenter)="activeIndex.set(index)"
        >
          <div class="result-text">
            <div class="result-title">
              @for (part of row.title | highlightWords: words(); track $index) {
                @if (part.hit) {
                  <mark>{{ part.text }}</mark>
                } @else {
                  {{ part.text }}
                }
              }
              @if (row.via) {
                <span class="result-via">via {{ row.via }}</span>
              }
            </div>
            @for (line of row.lines; track $index) {
              @if (line) {
                <div class="result-line">
                  @for (part of line | highlightWords: words(); track $index) {
                    @if (part.hit) {
                      <mark>{{ part.text }}</mark>
                    } @else {
                      {{ part.text }}
                    }
                  }
                </div>
              }
            }
          </div>
          @if (row.status; as status) {
            <span
              class="status-chip"
              [class]="status.cssClass"
              >{{ status.label }}</span
            >
          }
          @if (row.updatedAt) {
            <span
              class="result-updated"
              title="Last updated"
              >Updated {{ row.updatedAt | date: "MMM d, y" }}</span
            >
          }
        </div>
      }
    </div>
  } @empty {
    <p class="search-empty">
      @if (!words().length) {
        Start typing to search across JTMS.
      } @else if (isLoading()) {
        Searching…
      } @else {
        Nothing matches all of {{ words().join(", ") }}. Try fewer words.
      }
    </p>
  }
</div>

<div
  class="search-footer"
  aria-hidden="true"
>
  <span>↑ ↓ to move</span>
  <span>Enter to open</span>
  <span>Esc to close</span>
</div>
